import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RightToInformationGetStep3 } from '../../types/rightToInformationApi';
import type { RightToInformationWizardDraft } from '../../types/rightToInformationCaseRequest';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../wizard/criminal/CriminalReviewDocumentsTable';
import { useRightToInformationLookups } from '../wizard/rightToInformation/useRightToInformationLookups';
import { preferredAccessLabels } from '../wizard/rightToInformation/rightToInformationHelpers';
import { apiOrLookupLabel, submitterFromApiStep } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';

type Props = {
  caseRequestId: string;
  draft: RightToInformationWizardDraft;
  submittedAt: string;
  step3?: RightToInformationGetStep3;
  hideUploadDocumentsStep?: boolean;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function ViewRequestRightToInformationCaseDetailsTab({
  caseRequestId: _caseRequestId,
  draft,
  submittedAt,
  step3,
  hideUploadDocumentsStep = false,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useRightToInformationLookups('review');

  const preferredAccessDisplay = useMemo(
    () =>
      draft.preferredMethodOfAccessNames.filter(Boolean).join(', ') ||
      preferredAccessLabels(draft.preferredMethodOfAccessIds, lookups.preferredMethodOfAccess).join(', ') ||
      noData,
    [draft, lookups.preferredMethodOfAccess, noData],
  );

  const submitter = useMemo(() => submitterFromApiStep(step3), [step3]);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Requester Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Name of Requestor" value={draft.requestorName} />
            <ReviewField label="NIC/ Passport Number" value={draft.nicOrPassportNumber} />
            <ReviewField label="Contact Number" value={draft.contactNumber} />
            <ReviewField label="Email Address" value={draft.emailAddress} />
            <ReviewField
              label="Is the requestor a citizen of Sri Lanka?"
              value={yesNoLabel(draft.isSriLankanCitizen)}
            />
          </ReviewGrid>
          <ReviewField label="Address" value={draft.address} fullWidth />
        </ReviewSubBlock>
        <ReviewSubBlock title="Information Request Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Specific Public Authority Name" value={draft.specificPublicAuthorityName} />
            <ReviewField label="Information Requested Time Period" value={draft.informationRequestedTimePeriod} />
          </ReviewGrid>
          <ReviewField label="Requested Information Details" value={draft.requestedInformationDetails} fullWidth />
        </ReviewSubBlock>
        <ReviewSubBlock title="Additional Information">
          <ReviewField label="Preferred Method of Access" value={preferredAccessDisplay} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Preferred Language for Response"
              value={apiOrLookupLabel(
                draft.preferredLanguageName,
                draft.preferredLanguageId,
                lookups.preferredLanguages,
              )}
            />
            <ReviewField
              label="Does this request involve life or personal liberty of a citizen?"
              value={yesNoLabel(draft.involvesLifeOrPersonalLiberty)}
            />
          </ReviewGrid>
          <ReviewField label="Justification" value={draft.justification} fullWidth />
          <ReviewField label="Additional Notes" value={draft.additionalNotes} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Timeline">
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Submission">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      {!hideUploadDocumentsStep ? (
        <ReviewSection step="Step 04" title="Upload Documents">
          <ReviewSubBlock>
            <CriminalReviewDocumentsTable documents={draft.documents} />
          </ReviewSubBlock>
        </ReviewSection>
      ) : null}
    </div>
  );
}
